import {
  Table, Thead, Tbody, Tr, Th, Td, Button, useDisclosure, Box,
  Image, Tooltip, Input, Flex, Grid, GridItem, Text, Badge,
  Collapse, IconButton, VStack, HStack, Divider, Avatar,
  AvatarGroup, Wrap, WrapItem, Modal, ModalOverlay, ModalContent,
  ModalCloseButton, ModalBody, Icon
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { FaFilePdf } from 'react-icons/fa';
import FloatingAddButton from '../../components/FloatingAddButton';
import { useState, useEffect } from 'react';
import ModalBitacora from './ModalBitacora';
import { Bitacora } from './types';

const TablaBitacoras = () => {
  const [bitacoras, setBitacoras] = useState<Bitacora[]>([]);
  const [bitacorasFiltradas, setBitacorasFiltradas] = useState<Bitacora[]>([]);
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [modo, setModo] = useState<'view' | 'edit' | 'new'>('new');
  const [registro, setRegistro] = useState<Bitacora | null>(null);
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());
  const [imagenSeleccionada, setImagenSeleccionada] = useState<string | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isImageOpen, onOpen: onImageOpen, onClose: onImageClose } = useDisclosure();

  const parseJSONField = (field: any) => {
    if (typeof field === 'string') {
      try {
        // Primer intento de parsing
        const first = JSON.parse(field);
        // Si el resultado es un string, intentar parsear de nuevo (doble encoding)
        if (typeof first === 'string') {
          try {
            return JSON.parse(first);
          } catch {
            return first;
          }
        }
        return first;
      } catch {
        return field;
      }
    }
    return field;
  };

  const obtenerBitacoras = () => {
    fetch('https://totolac-backend.togui.com.mx/api/bitacoras')
      .then(res => res.json())
      .then(data => {
        const parsed = data.map((b: any) => {
          const parsedItem = {
            ...b,
            personal: parseJSONField(b.personal),
            ambulancias: parseJSONField(b.ambulancias),
            materialUtilizado: parseJSONField(b.materialUtilizado),
            evidenciaFotos: parseJSONField(b.evidenciaFotos),
            // Extraer la hora literal (HH:mm) sin conversión de zona horaria.
            // El API la guarda como UTC (ej. "1970-01-01T08:30:00.000Z"); usar
            // new Date()+toLocaleTimeString restaba 6h y cambiaba la hora.
            horaSalida: b.horaSalida ? String(b.horaSalida).split('T')[1]?.slice(0, 5) || '' : '',
            horaTermino: b.horaTermino ? String(b.horaTermino).split('T')[1]?.slice(0, 5) || '' : ''
          };

          return parsedItem;
        });
        setBitacoras(parsed);
        setBitacorasFiltradas(parsed);
      })
      .catch(err => console.error('Error al cargar bitácoras:', err));
  };

  useEffect(() => {
    obtenerBitacoras();
  }, []);

  const abrirModal = (modo: 'view' | 'edit' | 'new', registro?: Bitacora) => {
    setModo(modo);
    setRegistro(registro || null);
    onOpen();
  };

  const cerrarModal = () => {
    onClose();
    obtenerBitacoras();
  };

  const aplicarFiltro = () => {
    if (!fechaInicio && !fechaFin) {
      setBitacorasFiltradas(bitacoras);
      return;
    }

    const desde = fechaInicio ? new Date(fechaInicio) : null;
    const hasta = fechaFin ? new Date(fechaFin) : null;

    const filtradas = bitacoras.filter(b => {
      const fecha = new Date(b.fechaServicio);
      if (desde && fecha < desde) return false;
      if (hasta && fecha > hasta) return false;
      return true;
    });

    setBitacorasFiltradas(filtradas);
  };

  const toggleExpand = (id: number) => {
    const newExpanded = new Set(expandedCards);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedCards(newExpanded);
  };

  const getTipoColor = (tipo: string) => {
    switch (tipo) {
      case 'Médico clínico': return '#bb8c52';
      case 'Médico traumático': return '#5b182b';
      default: return '#691f30';
    }
  };

  const abrirImagen = (foto: string) => {
    const urlCompleta = foto.startsWith('http') ? foto : `https://totolac-backend.togui.com.mx${foto}`;
    setImagenSeleccionada(urlCompleta);
    onImageOpen();
  };

  const generarReportePDF = () => {
    const fecha = new Date();
    const fechaTexto = `${fecha.getDate().toString().padStart(2, '0')}/${(fecha.getMonth() + 1).toString().padStart(2, '0')}/${fecha.getFullYear()}`;
    
    let rangoFechas = '';
    if (fechaInicio && fechaFin) {
      const formatearFecha = (fechaISO: string) => {
        const [año, mes, dia] = fechaISO.split('-');
        return `${dia}/${mes}/${año}`;
      };
      rangoFechas = `${formatearFecha(fechaInicio)} - ${formatearFecha(fechaFin)}`;
    } else if (bitacorasFiltradas.length > 0) {
      const fechasValidas = bitacorasFiltradas
        .map(b => b.fechaServicio?.split('T')[0])
        .filter(Boolean)
        .sort();
      
      if (fechasValidas.length > 0) {
        const fechaMasAntigua = fechasValidas[0];
        const fechaMasReciente = fechasValidas[fechasValidas.length - 1];
        const formatearFecha = (fechaISO: string) => {
          const [año, mes, dia] = fechaISO.split('-');
          return `${dia}/${mes}/${año}`;
        };
        rangoFechas = fechaMasAntigua === fechaMasReciente 
          ? formatearFecha(fechaMasAntigua) 
          : `${formatearFecha(fechaMasAntigua)} - ${formatearFecha(fechaMasReciente)}`;
      } else {
        rangoFechas = 'Sin fechas disponibles';
      }
    } else {
      rangoFechas = 'Sin datos';
    }
    
    const totalBitacoras = bitacorasFiltradas.length;
    const tiposServicio = bitacorasFiltradas.reduce((acc: Record<string, number>, b) => {
      const tipo = b.tipoServicio || 'Sin especificar';
      acc[tipo] = (acc[tipo] || 0) + 1;
      return acc;
    }, {});
    const topTipos = Object.entries(tiposServicio)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 3)
      .map(([tipo, count]) => `${tipo}: ${count}`)
      .join(', ');
    
    const bitacorasOrdenadas = [...bitacorasFiltradas].sort((a, b) => {
      const fechaA = a.fechaServicio || '';
      const fechaB = b.fechaServicio || '';
      return fechaB.localeCompare(fechaA);
    });
    
    const cards = bitacorasOrdenadas.map(b => {
      const personalTexto = Array.isArray(b.personal) ? b.personal.join(', ') : (b.personal || 'No especificado');
      const ambulanciasTexto = Array.isArray(b.ambulancias) ? b.ambulancias.join(', ') : (b.ambulancias || 'No especificado');
      const materialTexto = Array.isArray(b.materialUtilizado) ? b.materialUtilizado.join(', ') : (b.materialUtilizado || 'No especificado');
      
      // Generar HTML para las imágenes de evidencia
      let imagenesHTML = '';
      if (Array.isArray(b.evidenciaFotos) && b.evidenciaFotos.length > 0) {
        const imagenes = b.evidenciaFotos.map(foto => {
          const urlCompleta = foto.startsWith('http') ? foto : `https://totolac-backend.togui.com.mx${foto}`;
          return `<img src="${urlCompleta}" alt="Evidencia" class="evidencia-img">`;
        }).join('');
        imagenesHTML = `
          <div class="evidencia-section">
            <strong>Evidencia Fotográfica:</strong>
            <div class="imagenes-grid">
              ${imagenes}
            </div>
          </div>
        `;
      } else {
        imagenesHTML = `
          <div class="info-item">
            <strong>Evidencia Fotográfica:</strong> Sin evidencia
          </div>
        `;
      }
      
      return `
        <div class="bitacora-card">
          <div class="card-header">
            <div class="tipo-badge" style="background-color: ${getTipoColor(b.tipoServicio)}">${b.tipoServicio}</div>
            <div class="fecha-info">
              <strong>Fecha:</strong> ${b.fechaServicio?.substring(0, 10) || 'No especificada'}
              <br><strong>Horario:</strong> ${b.horaSalida} - ${b.horaTermino}
            </div>
          </div>
          <div class="card-content">
            <div class="info-grid">
              <div class="info-item">
                <strong>Ubicación:</strong> ${b.ubicacion || 'No especificada'}
              </div>
              <div class="info-item">
                <strong>Municipio:</strong> ${b.municipio || 'No especificado'}
              </div>
              <div class="info-item">
                <strong>Dirección:</strong> ${b.direccion || 'No especificada'}
              </div>
              <div class="info-item">
                <strong>Personal:</strong> ${personalTexto}
              </div>
              <div class="info-item">
                <strong>Ambulancias:</strong> ${ambulanciasTexto}
              </div>
              <div class="info-item">
                <strong>Descripción del Apoyo:</strong> ${b.descripcionApoyo || 'No especificada'}
              </div>
              <div class="info-item">
                <strong>Detalles de Atención:</strong> ${b.detallesAtencion || 'No especificados'}
              </div>
              <div class="info-item">
                <strong>Observaciones:</strong> ${b.observaciones || 'Sin observaciones'}
              </div>
              <div class="info-item">
                <strong>Apoyos Adicionales:</strong> ${b.apoyosAdicionales || 'Ninguno'}
              </div>
              <div class="info-item">
                <strong>Datos Clínicos:</strong> ${b.datosClinicos || 'No especificados'}
              </div>
              <div class="info-item">
                <strong>Material Utilizado:</strong> ${materialTexto}
              </div>
              <div class="info-item">
                <strong>Evidencia Material:</strong> ${b.evidenciaMaterial || 'No especificada'}
              </div>
            </div>
            ${imagenesHTML}
          </div>
        </div>
      `;
    }).join('');

    const html = `
      <!DOCTYPE html>
      <html lang="es">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Reporte de Bitácoras</title>
          <style>
            @media print {
              @page {
                margin: 1.5cm;
                size: A4;
              }
              body {
                margin: 0;
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
              }
            }
            
            * {
              box-sizing: border-box;
            }
            
            body {
              font-family: 'Arial', sans-serif;
              margin: 0;
              padding: 15px;
              background: white;
              color: #333;
              line-height: 1.4;
              font-size: 11px;
            }
            
            .container {
              max-width: 100%;
              margin: 0 auto;
            }
            
            .header {
              margin-bottom: 25px;
              padding-bottom: 15px;
              border-bottom: 3px solid #5b182b;
            }
            
            .logo-section {
              display: flex;
              align-items: center;
              margin-bottom: 15px;
            }
            
            .logo {
              width: 120px;
              height: 80px;
              margin-right: 20px;
            }
            
            .header-text {
              flex: 1;
            }
            
            .title {
              color: #5b182b;
              font-size: 20px;
              font-weight: bold;
              margin: 0 0 5px 0;
              text-transform: uppercase;
            }
            
            .subtitle {
              color: #666;
              font-size: 14px;
              margin: 0 0 8px 0;
            }
            
            .date-range {
              color: #bb8c52;
              font-size: 16px;
              font-weight: bold;
              margin: 0;
            }
            
            .statistics {
              background: #f8f9fa;
              padding: 15px;
              border-radius: 5px;
              border: 2px solid #5b182b;
              margin: 20px 0;
              text-align: center;
            }
            
            .stat-title {
              color: #5b182b;
              font-size: 16px;
              font-weight: bold;
              margin: 0 0 10px 0;
              text-transform: uppercase;
            }
            
            .stat-info {
              color: #666;
              font-size: 12px;
              margin-bottom: 10px;
            }
            
            .bitacora-card {
              background: white;
              border: 1px solid #ddd;
              border-radius: 8px;
              margin-bottom: 20px;
              overflow: hidden;
              page-break-inside: avoid;
            }
            
            .card-header {
              background: #5b182b;
              color: white;
              padding: 12px;
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            
            .tipo-badge {
              color: white;
              padding: 4px 12px;
              border-radius: 15px;
              font-size: 10px;
              font-weight: bold;
            }
            
            .fecha-info {
              font-size: 11px;
              text-align: right;
            }
            
            .card-content {
              padding: 15px;
            }
            
            .info-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 10px;
            }
            
            .info-item {
              margin-bottom: 8px;
              font-size: 10px;
            }
            
            .info-item strong {
              color: #5b182b;
              display: block;
              margin-bottom: 2px;
            }
            
            .evidencia-section {
              margin-top: 15px;
              padding-top: 15px;
              border-top: 1px solid #eee;
            }
            
            .evidencia-section strong {
              color: #5b182b;
              display: block;
              margin-bottom: 10px;
              font-size: 11px;
            }
            
            .imagenes-grid {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
              gap: 10px;
              margin-top: 10px;
            }
            
            .evidencia-img {
              width: 100%;
              height: 200px;
              object-fit: cover;
              border-radius: 4px;
              border: 1px solid #ddd;
            }
            
            .footer {
              text-align: center;
              margin-top: 25px;
              padding-top: 15px;
              border-top: 1px solid #dee2e6;
              font-size: 10px;
              color: #666;
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <div class="logo-section">
                <img src="/img/logo.png" alt="Logo Ayuntamiento" class="logo">
                <div class="header-text">
                  <h1 class="title">Reporte de Bitácoras de Servicios</h1>
                  <h2 class="subtitle">H. Ayuntamiento de Totolac, Tlaxcala</h2>
                  <h3 class="date-range">Periodo: ${rangoFechas}</h3>
                </div>
              </div>
            </div>
            
            <div class="statistics">
              <div class="stat-title">Resumen del Reporte</div>
              <div class="stat-info">Total de bitácoras: <strong>${totalBitacoras}</strong></div>
              <div class="stat-info">Tipos de servicio: ${topTipos}</div>
            </div>
            
            ${cards}
            
            <div class="footer">
              <p><strong>H. Ayuntamiento de Totolac</strong></p>
              <p>Protección Civil - Tlaxcala, México</p>
              <p>Documento generado automáticamente el ${fechaTexto}</p>
            </div>
          </div>
          
          <script>
            window.onload = function() {
              window.focus();
              setTimeout(() => {
                window.print();
              }, 1000);
            };
          </script>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank', 'width=1200,height=800');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
    } else {
      alert('No se pudo abrir la ventana de impresión. Verifica que los popups estén habilitados.');
    }
  };

  return (
    <Box p={4} pt={79} position="relative">
      <Flex gap={4} mb={4}>
        <Input
          type="date"
          value={fechaInicio}
          onChange={(e) => setFechaInicio(e.target.value)}
          max={fechaFin}
        />
        <Input
          type="date"
          value={fechaFin}
          onChange={(e) => setFechaFin(e.target.value)}
          min={fechaInicio}
        />
        <Button onClick={aplicarFiltro} bg="#691f30" color="white" _hover={{ bg: '#5b182b' }}>
          Filtrar
        </Button>
        <Button onClick={() => {
          setFechaInicio('');
          setFechaFin('');
          setBitacorasFiltradas(bitacoras);
        }} bg="#bb8c52" color="white" _hover={{ bg: '#a67a47' }}>
          Limpiar
        </Button>
      </Flex>
      
      <Flex justify="flex-end" mb={4}>
        <Button
          onClick={generarReportePDF}
          bg="#5b182b"
          color="white"
          _hover={{ bg: "#691f30" }}
          leftIcon={<Icon as={FaFilePdf} />}
        >
          Generar PDF
        </Button>
      </Flex>

      <FloatingAddButton 
        text="Nueva Bitácora" 
        onClick={() => abrirModal('new')} 
      />

      <VStack spacing={4} align="stretch">
        {bitacorasFiltradas.map((b) => (
          <Box
            key={b.id}
            bg="white"
            borderRadius="lg"
            boxShadow="md"
            border="1px solid"
            borderColor="gray.200"
            overflow="hidden"
            _hover={{ boxShadow: 'lg', transform: 'translateY(-2px)' }}
            transition="all 0.2s"
          >
            {/* Header de la card */}
            <Box bg="#5b182b" color="white" p={4}>
              <Grid templateColumns="1fr auto auto" gap={4} alignItems="center">
                <GridItem>
                  <HStack spacing={3}>
                    <Badge 
                      bg={getTipoColor(b.tipoServicio)} 
                      color="white" 
                      px={3} 
                      py={1} 
                      borderRadius="full"
                      fontSize="sm"
                    >
                      {b.tipoServicio}
                    </Badge>
                    <Text fontSize="lg" fontWeight="bold">
                      {b.fechaServicio?.substring(0, 10)}
                    </Text>
                    <Text fontSize="sm" opacity={0.9}>
                      {b.horaSalida} - {b.horaTermino}
                    </Text>
                  </HStack>
                </GridItem>
                <GridItem>
                  <HStack spacing={2}>
                    <Button 
                      size="sm" 
                      bg="#bb8c52" 
                      color="white" 
                      _hover={{ bg: '#a67a47' }}
                      onClick={() => abrirModal('view', b)}
                    >
                      Ver
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      borderColor="white" 
                      color="white"
                      _hover={{ bg: 'rgba(255,255,255,0.1)' }}
                      onClick={() => abrirModal('edit', b)}
                    >
                      Editar
                    </Button>
                  </HStack>
                </GridItem>
                <GridItem>
                  <IconButton
                    aria-label="Expandir"
                    icon={expandedCards.has(b.id) ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    size="sm"
                    variant="ghost"
                    color="white"
                    _hover={{ bg: 'rgba(255,255,255,0.01)' }}
                    onClick={() => toggleExpand(b.id)}
                  />
                </GridItem>
              </Grid>
            </Box>

            {/* Contenido principal */}
            <Box p={4}>
              <Grid templateRows="repeat(2, 1fr)"
                    templateColumns="repeat(5, 1fr)" gap={2}>
                {/* Columna izquierda */}
                <GridItem rowSpan={2} colSpan={1}>
                  {/* Ubicación, Municipio y Dirección */}
                  <Box mb={4}>
                    <Text fontSize="sm" color="#691f30" fontWeight="bold" mb={2}>UBICACIÓN</Text>
                    <HStack spacing={3} align="center" wrap="wrap">
                      <Text fontWeight="medium">{b.ubicacion}</Text>                      
                    </HStack>
                  </Box>

                  {/* Personal completo */}
                  <Box mb={4}>
                    <Text fontSize="sm" color="#691f30" fontWeight="bold" mb={2}>
                      PERSONAL ({Array.isArray(b.personal) ? b.personal.length : 0})
                    </Text>
                    <Wrap spacing={1}>
                      {Array.isArray(b.personal) && b.personal.map((persona, idx) => (
                        <WrapItem key={idx}>
                          <Badge bg="#bb8c52" color="white" fontSize="xs">
                            {persona.replace('Operativo. ', '').replace('Operativa. ', '').replace('COORDINADOR DE PROTECCIÓN CIVIL ', 'COORD. ')}
                          </Badge>
                        </WrapItem>
                      ))}
                    </Wrap>
                  </Box>                
                </GridItem>
                {/* Municipio y Dirección */}
                <GridItem colSpan={2}>
                    <HStack><Text fontSize="sm" color="#691f30" fontWeight="bold" >MUNICIPIO</Text>
                      <Badge bg="#681a2d" color="white" fontSize="sm">
                        {b.municipio || 'No especificado'}
                      </Badge>
                      {b.direccion && (
                        <Text fontSize="sm" color="gray.500">- {b.direccion}</Text>
                      )}

                    </HStack>
                  </GridItem>

                <GridItem colSpan={2}>
                   {/* Ambulancias  */}
                  <Box mb={4}>
                    {b.ambulancias && (
                      <Text fontSize="sm" color="#691f30" fontWeight="bold" mb={2}>
                        AMBULANCIAS/UNIDADES ({Array.isArray(b.ambulancias) ? b.ambulancias.length : 0})
                      </Text>
                    )}
                    <Wrap spacing={1}>
                      {Array.isArray(b.ambulancias) && b.ambulancias.map((ambulancia, idx) => (
                        <WrapItem key={idx}>
                          <Badge bg="#681a2d" color="white" fontSize="xs">
                            {ambulancia}
                          </Badge>
                        </WrapItem>
                      ))}
                    </Wrap>
                  </Box>

                  {/* Descripción */}
                  <Box>
                    <Text fontSize="sm" color="#691f30" fontWeight="bold" mb={2}>DESCRIPCIÓN DEL APOYO</Text>
                    <Text fontSize="sm" noOfLines={expandedCards.has(b.id) ? undefined : 3}>
                      {b.descripcionApoyo}
                    </Text>
                  </Box>
                </GridItem>

                {/* Columna derecha - Evidencias */}
                <GridItem colSpan={2} >
                  <VStack align="start" spacing={2}>
                    <Box width="100%">
                      <Text fontSize="sm" color="#691f30" fontWeight="bold" mb={2}>
                        EVIDENCIAS ({Array.isArray(b.evidenciaFotos) ? b.evidenciaFotos.length : 0})
                      </Text>
                      <HStack spacing={2} align="start" wrap="wrap">
                        {Array.isArray(b.evidenciaFotos) &&
                          b.evidenciaFotos.slice(0, 8).map((foto, idx) => (
                            <Tooltip label="Clic para ampliar" key={idx}>
                              <Image
                                src={
                                  foto.startsWith('http')
                                    ? foto
                                    : `https://totolac-backend.togui.com.mx${foto}`
                                }
                                alt={`evidencia-${idx}`}
                                boxSize="60px"
                                objectFit="cover"
                                borderRadius="md"
                                border="2px solid"
                                borderColor="#bb8c52"
                                cursor="pointer"
                                onClick={() => abrirImagen(foto)}
                                _hover={{ transform: 'scale(1.05)', borderColor: '#691f30' }}
                                transition="all 0.2s"
                                flexShrink={0}
                              />
                            </Tooltip>
                          ))}
                        {Array.isArray(b.evidenciaFotos) && b.evidenciaFotos.length > 8 && (
                          <Box
                            boxSize="60px"
                            bg="#bb8c52"
                            color="white"
                            borderRadius="md"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            fontSize="xs"
                            fontWeight="bold"
                            flexShrink={0}
                          >
                            +{b.evidenciaFotos.length - 8}
                          </Box>
                        )}
                      </HStack>
                    </Box>
                  </VStack>
                </GridItem>
              </Grid>



              {/* Sección expandible */}
              <Collapse in={expandedCards.has(b.id)}>
                <Box mt={2} pt={2} borderTop="2px solid" borderColor="#bb8c52">
                  <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={6}>
                    <GridItem>
                      <VStack align="start" spacing={4}>
                        {/* Material Utilizado */}
                        {Array.isArray(b.materialUtilizado) && b.materialUtilizado.length > 0 && (
                          <Box>
                            <Text fontSize="sm" color="#5b182b" fontWeight="bold" mb={2}>MATERIAL UTILIZADO</Text>
                            <Wrap>
                              {b.materialUtilizado.map((material, idx) => (
                                <WrapItem key={idx}>
                                  <Badge bg="#bb8c52" color="white" fontSize="xs">
                                    {material}
                                  </Badge>
                                </WrapItem>
                              ))}
                            </Wrap>
                          </Box>
                        )}
                        
                        {b.detallesAtencion && (
                          <Box>
                            <Text fontSize="sm" color="#5b182b" fontWeight="bold" mb={2}>DETALLES DE ATENCIÓN</Text>
                            <Text fontSize="sm">{b.detallesAtencion}</Text>
                          </Box>
                        )}

                        {b.apoyosAdicionales && (
                          <Box>
                            <Text fontSize="sm" color="#5b182b" fontWeight="bold" mb={2}>APOYOS ADICIONALES</Text>
                            <Text fontSize="sm">{b.apoyosAdicionales}</Text>
                          </Box>
                        )}
                      </VStack>
                    </GridItem>
                    <GridItem>
                      <VStack align="start" spacing={4}>
                        {b.observaciones && (
                          <Box>
                            <Text fontSize="sm" color="#5b182b" fontWeight="bold" mb={2}>OBSERVACIONES</Text>
                            <Text fontSize="sm">{b.observaciones}</Text>
                          </Box>
                        )}
                        
                        {b.datosClinicos && (
                          <Box>
                            <Text fontSize="sm" color="#5b182b" fontWeight="bold" mb={2}>DATOS CLÍNICOS</Text>
                            <Text fontSize="sm">{b.datosClinicos}</Text>
                          </Box>
                        )}

                        {b.evidenciaMaterial && (
                          <Box>
                            <Text fontSize="sm" color="#5b182b" fontWeight="bold" mb={2}>EVIDENCIA MATERIAL</Text>
                            <Text fontSize="sm">{b.evidenciaMaterial}</Text>
                          </Box>
                        )}
                      </VStack>
                    </GridItem>
                  </Grid>
                </Box>
              </Collapse>
            </Box>
          </Box>
        ))}
      </VStack>

      <ModalBitacora
        isOpen={isOpen}
        onClose={cerrarModal}
        modo={modo}
        data={registro}
        onGuardar={() => obtenerBitacoras()}
      />

      {/* Modal para ampliar imagen */}
      <Modal isOpen={isImageOpen} onClose={onImageClose} size="xl" isCentered>
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent bg="transparent" boxShadow="none" maxW="90vw" maxH="90vh">
          <ModalCloseButton color="white" bg="#bb8c52 " _hover={{ bg: '#bb8c52 ' }} size="lg" />
          <ModalBody p={0} display="flex" justifyContent="center" alignItems="center" minH="90vh">
            {imagenSeleccionada && (
              <Image
                src={imagenSeleccionada}
                alt="Evidencia ampliada"
                maxW="100%"
                maxH="90vh"
                objectFit="contain"
                borderRadius="md"
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default TablaBitacoras;
